import * as React from 'react';
import { HelpButton } from '@chess/api-help/react';
import { CardSlotsContext, type CardSlots, type DashboardCardProps } from '@chess/api-dashboard/react';

// The frame every dashboard card shares: the title in the head, the card's
// aside and its help "?" at the head's right, the body, and the note as the
// last line at the card's bottom edge. A card plugin draws only its body;
// the section wraps it in this frame (registerCard, @chess/api-dashboard).
// The body puts its aside and note here through `CardAside`/`CardNote`,
// which portal into the head's and the note's elements; those are taken by
// callback refs, so the portals fill in the same commit, before paint, and
// a skeleton's height never jumps. An aside, note or tools row left empty
// is not shown (dashboard.css).

export type { DashboardCardProps };

export const DashboardCard: React.FC<DashboardCardProps> = ({ title, help, wide, aside, note, children }) => {
  const [asideEl, setAside] = React.useState<HTMLElement | null>(null);
  const [noteEl, setNote] = React.useState<HTMLElement | null>(null);
  const slots = React.useMemo<CardSlots>(() => ({ aside: asideEl, note: noteEl }), [asideEl, noteEl]);
  return (
    <section className={`card dash-card viz dash-card-frame${wide ? ' dash-card-wide' : ''}`}>
      <div className="dash-card-head">
        <h3 className="dash-card-title">{title}</h3>
        <span className="dash-card-tools">
          <span className="dash-card-aside" ref={setAside}>
            {aside}
          </span>
          {help && <HelpButton topic={help} />}
        </span>
      </div>
      <div className="dash-card-body">
        <CardSlotsContext.Provider value={slots}>{children}</CardSlotsContext.Provider>
      </div>
      <p className="dash-card-note" ref={setNote}>
        {note}
      </p>
    </section>
  );
};
